<script setup lang="ts">
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { computed, ref } from 'vue';
import { RouteName } from '@/data/routes';
import { useInvitedUserRouting } from '@/composables/useInvitedUserRouting';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';

import DSButton from 'primevue/button';

const router = useRouter();
const store = useStore();

const props = defineProps<{
  disabled?: boolean;
  onNext?: (routeName: RouteName) => Promise<boolean>;
}>();

const loading = ref(false);

const currentRoute = computed(() => router.currentRoute.value.name as string);

const { getAdjacentInvitedUserOnboardingRoutes } = useInvitedUserRouting();

const { previousRouteName, nextRouteName, fallbackRouteName } =
  getAdjacentInvitedUserOnboardingRoutes();

const handleBackClick = () => {
  router.push({ name: previousRouteName ?? fallbackRouteName });
};

const handleNextClick = async () => {
  loading.value = true;
  const routeName: RouteName = nextRouteName ?? fallbackRouteName;

  let shouldRedirect = true;
  if (props.onNext) {
    try {
      shouldRedirect = await props.onNext(routeName);
    } catch (error) {
      shouldRedirect = false;
      console.warn('Error navigating to next step:', error);
    }
  }
  loading.value = false;
  if (shouldRedirect) {
    store.dispatch('UPDATE_INVITED_USER_ONBOARDING_STEP', currentRoute.value);
    analytics.track(SEGMENT_EVENTS.if_invited_user_onboarding_page_completed, {
      page: currentRoute.value,
    });
    router.push({ name: routeName });
  }
};

defineExpose({ handleNextClick });
</script>

<template>
  <div class="c-form__row mt-8" :class="$style['button-container']">
    <DSButton
      v-if="previousRouteName"
      label="Back"
      :class="$style['back-button']"
      class="w-full p-button-outlined"
      @click="handleBackClick"
    />
    <DSButton
      label="Next"
      class="w-full"
      @click="handleNextClick"
      :loading="loading"
      :disabled="props.disabled"
    />
  </div>
</template>

<style module>
.button-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 20px;
}
</style>
